import { transactionApi } from './transactionApi'
import { adapterTransaction } from './transactionAdapters'
import {
  GetTransactionsParams,
  GetTransactionsAdaptedParams,
  ApiTransaction,
  Transaction,
  TransactionWithDateHeader,
} from './transactionTypes'

/**
 * Transform API transaction to domain transaction with formatting
 */
function transformApiTransaction(apiTransaction: ApiTransaction): Transaction {
  return adapterTransaction(apiTransaction)
}

/**
 * Normalize date to UTC start/end of day for API requests
 * This ensures dates are interpreted correctly regardless of timezone
 */
function normalizeDateToUTC(date: Date, isEndDate: boolean = false): number {
  // Get local date components
  const year = date.getFullYear()
  const month = date.getMonth()
  const day = date.getDate()

  if (isEndDate) {
    // End date: end of day in UTC (23:59:59.999 UTC)
    const utcDate = new Date(Date.UTC(year, month, day, 23, 59, 59, 999))
    return utcDate.getTime()
  } else {
    // Start date: beginning of day in UTC (00:00:00 UTC)
    const utcDate = new Date(Date.UTC(year, month, day, 0, 0, 0, 0))
    return utcDate.getTime()
  }
}

/**
 * Get transactions with domain transformation (adapted from RN)
 */
async function getTransactions({
  from_date,
  to_date,
  ...restParams
}: GetTransactionsAdaptedParams = {}): Promise<{
  transactions: Transaction[]
  next_page_id: number | null
}> {
  // Convert dates to timestamps with UTC normalization to avoid timezone issues
  const apiParams: GetTransactionsParams = {
    ...restParams,
    from_date: from_date
      ? normalizeDateToUTC(new Date(from_date), false)
      : undefined,
    to_date: to_date ? normalizeDateToUTC(new Date(to_date), true) : undefined,
  }

  const response = await transactionApi.getTransactions(apiParams)

  const transactions = response.transactions.map(transformApiTransaction)

  return {
    transactions,
    next_page_id: response.next_page_id,
  }
}

/**
 * Get single transaction with domain transformation
 */
async function getTransactionById(transactionId: string): Promise<Transaction> {
  const apiTransaction = await transactionApi.getTransactionById(transactionId)
  return transformApiTransaction(apiTransaction)
}

/**
 * Add date headers to transactions list
 */
function addDateHeadersToTransactions(
  transactions: Transaction[],
): TransactionWithDateHeader[] {
  const result: TransactionWithDateHeader[] = []
  let currentDateString = ''

  transactions.forEach((transaction) => {
    const transactionDateString = transaction.created_at.toDateString()

    // Add date header if it's a new date
    if (transactionDateString !== currentDateString) {
      result.push({
        type: 'date-header',
        date: transactionDateString,
      })
      currentDateString = transactionDateString
    }

    // Add transaction
    result.push({
      type: 'transaction',
      transaction,
    })
  })

  return result
}

/**
 * Format transaction amount for display
 */
function formatTransactionAmount(transaction: Transaction): {
  amount: string
  currency: string
  fiatAmount: string
  fiatCurrency: string
} {
  const { amount } = transaction

  return {
    amount: parseFloat(amount.amount)
      .toFixed(8)
      .replace(/\.?0+$/, ''),
    currency: amount.currency,
    fiatAmount: parseFloat(amount.fiat_amount).toFixed(2),
    fiatCurrency: amount.fiat_currency,
  }
}

/**
 * Get transaction title based on type and direction
 * @param transaction - Transaction object
 * @param t - Translation function from react-i18next
 */
function getTransactionTitle(
  transaction: Transaction,
  t: (key: string, options?: any) => string,
): string {
  const { type, direction, subject_user } = transaction

  switch (type) {
    case 'kem_to_kem':
    case 'on_ramp_send':
      return direction === 'send'
        ? `${t('translation:sentTo')} ${subject_user?.first_name || subject_user?.phone_number || t('translation:unnamed')}`
        : `${t('translation:receivedFrom')} ${subject_user?.first_name || subject_user?.phone_number || t('translation:unnamed')}`

    case 'on_ramp_top_up':
      // Currency code at the end for proper RTL display
      return `${transaction.amount?.currency || ''} ${t('translation:bought')}`

    case 'external_deposit':
      // Currency code at the end for proper RTL display
      return `${transaction.amount?.currency || ''} ${t('translation:deposited')}`

    case 'external_withdraw':
      // Capitalize first letter like in RN, currency code at the end for proper RTL display
      const withdrew = t('translation:withdrew')
      return `${transaction.amount?.currency || ''} ${withdrew.charAt(0).toUpperCase() + withdrew.slice(1)}`

    case 'swap_convert':
      // For conversions, show "Converted to [target currency]" like in RN
      // Currency code at the end for proper RTL display
      if (transaction.subject_swap?.credit_amount?.currency) {
        const targetCurrency = transaction.subject_swap.credit_amount.currency
        return `${targetCurrency} ${t('translation:convertedTo')}`
      }
      return t('translation:converted')

    case 'swap_sell':
      // Currency code at the end for proper RTL display
      return `${transaction.amount?.currency || ''} ${t('translation:sold')}`

    case 'kem_reward':
      // Handle different reward types like in RN
      const rewardType = transaction.subject_reward?.reward_type?.toLowerCase()
      if (rewardType === 'signup') {
        return t('translation:signUpBonus')
      } else if (rewardType === 'referral') {
        return t('translation:referralBonus')
      }
      return t('translation:rewardNotMapped')

    case 'card_write_off':
      return t('translation:purchase')

    case 'card_top_up':
      return t('transactions:types.refund')

    case 'saving_transfer_in':
      return t('translation:goal.savingsDeposit')

    case 'saving_transfer_out':
      return t('translation:goal.savingsWithdrawal')

    case 'cashback_payout':
      return t('transactions:types.cashback')

    case 'payment_link':
      return t('transactions:types.paymentLink')

    case 'boxo_purchase':
      // Handle boxo purchase types based on order type
      if (transaction.subject_boxo_purchase?.boxo_order_type === 'esim') {
        return t('transactions:types.esimPurchase')
      }
      if (transaction.subject_boxo_purchase?.boxo_order_type === 'hotel') {
        return t('transactions:types.hotelPurchase')
      }
      if (transaction.subject_boxo_purchase?.boxo_order_type === 'flight') {
        return t('transactions:types.flightPurchase')
      }
      return t('translation:purchase')

    default:
      return direction === 'receive'
        ? t('transactions:types.topUp')
        : t('transactions:types.writeOff')
  }
}

export const transactionServices = {
  getTransactions,
  getTransactionById,
  addDateHeadersToTransactions,
  formatTransactionAmount,
  getTransactionTitle,
}
